import { capitalizeFirstLetter } from 'helpers/string';
import React, { useCallback, useMemo } from 'react';
import { useStyles } from './ThemeProvider';
import { set } from 'lodash';
import { momentInstance } from 'helpers/date-time';
import { toFixed } from 'helpers/number';

const ChartContext = React.createContext({
  renderSeriesChartByHourly: () => {},
});

export const useChart = () => React.useContext(ChartContext);

const ChartProvider = ({ children }) => {
  const { colorsSystem } = useStyles();

  const definedColorChart = useCallback(
    (type) => {
      let color = '';
      switch (type) {
        case 'sent':
          color = colorsSystem.brandPrimary;
          break;

        case 'delivered':
        case 'callback':
        case 'shortCall':
        case 'shortcall':
          color = colorsSystem.brandSuccess;
          break;

        case 'clicked':
          color = colorsSystem.brandClickedRate;
          break;

        case 'failed':
        case 'blocked':
          color = colorsSystem.brandDanger;
          break;

        case 'received':
          color = colorsSystem.brandReplied;
          break;

        case 'convertor':
          color = colorsSystem.brandConvertor;
          break;

        case 'revenue':
          color = '#2caffe';
          break;

        default:
          break;
      }

      return color;
    },
    [colorsSystem]
  );

  const renderSeriesChartByHourly = (dataChart = {}) => {
    if (!dataChart || !dataChart.length) {
      return [];
    }

    const fieldsForShowing = [
      'blocked',
      'clicked',
      'failed',
      'sent',
      'delivered',
      'convertor',
      'callback',
      'shortCall',
      'revenue',
    ];

    const filterByHour = dataChart.reduce((obj, el) => {
      obj = {
        ...obj,
        [el.datehour]: obj[el?.datehour] ? [...obj[el?.datehour], el] : [el],
      };
      return obj;
    }, {});

    const groupedDateHour = Object.entries(filterByHour).reduce((obj, [key, value]) => {
      const sumStats = (value || []).reduce((nextObj, el) => {
        fieldsForShowing.forEach((field) => {
          set(nextObj, field, nextObj?.[field] ? nextObj?.[field] + el?.[field] : el?.[field]);
        });

        return nextObj;
      }, {});

      obj = {
        ...obj,
        [key]: sumStats,
      };
      return obj;
    }, {});

    const result = Object.entries(groupedDateHour || {}).reduce((nextResult, [dateHour, stats]) => {
      fieldsForShowing.forEach((field) => {
        set(nextResult, field, [
          ...(nextResult?.[field] || []),
          [momentInstance.utc(dateHour, 'YYMMDDHH').unix() * 1000, toFixed(stats?.[field], 2)],
        ]);
      });

      return nextResult;
    }, {});

    const series = Object.entries(result).map(([typeStats, dataEachTypeStats]) => {
      const isRevenue = typeStats === 'revenue';
      const template = {
        type: isRevenue ? 'column' : 'spline',
        marker: {
          enabled: true,
          radius: 3,
          symbol: 'circle',
        },
        data: dataEachTypeStats,
        name: capitalizeFirstLetter(typeStats),
        color: definedColorChart(typeStats),
        yAxis: isRevenue ? 1 : 0,
        maxPointWidth: isRevenue ? 40 : undefined,
        zIndex: isRevenue ? 1 : 2,
      };

      return template;
    });

    return series || [];
  };

  const value = useMemo(() => {
    return {
      renderSeriesChartByHourly,
    };
  }, [renderSeriesChartByHourly]);

  return <ChartContext.Provider value={value}>{children}</ChartContext.Provider>;
};

export default ChartProvider;
